import React, { useEffect, useRef, useState } from 'react';
import Cropper from 'react-easy-crop';
import * as XLSX from 'xlsx';
import DashboardLayout from '../../components/DashboardLayout';
import axiosInstance from '../../utils/axiosInstance';
import AuthService from '../../services/authService';
import Swal from 'sweetalert2';
import { getImageURL } from '../../utils/api';

// Helper: random password (10 chars)
const generatePassword = () => {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789@#$%';
  let out = '';
  for (let i = 0; i < 10; i += 1) {
    out += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return out;
};

// Map staff type to tailwind classes
const getStaffTypeColor = (type) => {
  const t = String(type || '').toLowerCase();
  if (!t) return 'bg-gray-100 text-gray-700 border-gray-200';
  if (t.includes('admin')) return 'bg-red-50 text-red-700 border-red-200';
  if (t.includes('coordinator') || t.includes('coord')) return 'bg-indigo-50 text-indigo-700 border-indigo-200';
  if (t.includes('lecturer') || t.includes('academic')) return 'bg-emerald-50 text-emerald-700 border-emerald-200';
  return 'bg-slate-50 text-slate-700 border-slate-200';
};

export default function Staff() {
  const [staff, setStaff] = useState([]);
  const [positions, setPositions] = useState([]);
  const [totalStaff, setTotalStaff] = useState(0);

  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [filterStatus, setFilterStatus] = useState('active');
  const [imageErrors, setImageErrors] = useState({});
  const [processing, setProcessing] = useState(null); // staffId currently being processed

  const [staffForImageUpload, setStaffForImageUpload] = useState(null);
  const [cropModalOpen, setCropModalOpen] = useState(false);
  const [cropSrc, setCropSrc] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [cropFileType, setCropFileType] = useState('image/jpeg');
  const fileInputRef = useRef(null);

  const user = AuthService.getUser();
  const userRole = user?.superAdmin?.staff?.role || user?.superAdmin?.role;
  const isSuperAdmin = Boolean(user?.superAdmin?.superAdminId);
  const isAdmin = isSuperAdmin || userRole === 'Admin' || userRole === 'Administrator';

  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [staffForEdit, setStaffForEdit] = useState(null);
  const [showDownloadMenu, setShowDownloadMenu] = useState(false);
  const downloadMenuRef = useRef(null);

  // Close download menu on outside click
  useEffect(() => {
    const handleOutsideClick = (e) => {
      if (downloadMenuRef.current && !downloadMenuRef.current.contains(e.target)) {
        setShowDownloadMenu(false);
      }
    };
    if (showDownloadMenu) document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, [showDownloadMenu]);

  const downloadStaffExcel = (filter) => {
    const dataToExport = filter === 'active'
      ? sortedStaff.filter((s) => s.loginStatus === 'active')
      : sortedStaff;

    if (!dataToExport.length) {
      Swal.fire('No Data', `No ${filter === 'active' ? 'active ' : ''}staff found to export.`, 'info');
      setShowDownloadMenu(false);
      return;
    }

    const rows = dataToExport.map((s, idx) => ({
      '#': idx + 1,
      'Employee No.': s.employeeNumber || '',
      'Title': s.title || '',
      'Full Name': s.name || '',
      'Gender': s.gender ? s.gender.charAt(0).toUpperCase() + s.gender.slice(1) : '',
      'Position': s.position || '',
      'Staff Type': s.type || '',
      'Office Email': s.officeEmail || '',
      'Gmail': s.gmail || '',
      'Mobile Number': s.mobileNumber || '',
      'Status': s.loginStatus ? s.loginStatus.charAt(0).toUpperCase() + s.loginStatus.slice(1) : '',
    }));

    const worksheet = XLSX.utils.json_to_sheet(rows);

    // Column widths
    worksheet['!cols'] = [
      { wch: 5 },  // #
      { wch: 15 }, // Employee No.
      { wch: 8 },  // Title
      { wch: 25 }, // Full Name
      { wch: 10 }, // Gender
      { wch: 25 }, // Position
      { wch: 20 }, // Staff Type
      { wch: 30 }, // Office Email
      { wch: 30 }, // Gmail
      { wch: 15 }, // Mobile Number
      { wch: 10 }, // Status
    ];

    const workbook = XLSX.utils.book_new();
    const sheetName = filter === 'active' ? 'Active Staff' : 'All Staff';
    XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);

    const today = new Date().toISOString().slice(0, 10);
    const fileName = filter === 'active'
      ? `Active_Staff_${today}.xlsx`
      : `All_Staff_${today}.xlsx`;

    XLSX.writeFile(workbook, fileName);
    setShowDownloadMenu(false);
  };

  // Load staff + positions
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const resp = await axiosInstance.get('/admin/staff');
        if (cancelled) return;
        const data = resp?.data || {};
        setStaff(data.staff || []);
        setPositions(data.positions || []);
        setTotalStaff(data.totalStaff || (data.staff ? data.staff.length : 0));
      } catch (err) {
        if (cancelled) return;
        console.error('Failed to load staff', err);
        Swal.fire('Error', err?.response?.data?.message || 'Failed to load staff list', 'error');
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  // Derived lists - ensure consistent ordering by staffId (oldest -> newest)
  const sortedStaff = [...staff].sort((a, b) => {
    const idA = Number(a.staffId) || 0;
    const idB = Number(b.staffId) || 0;
    return idA - idB;
  });

  const filteredStaff = sortedStaff.filter((member) => {
    const term = searchTerm.trim().toLowerCase();
    const matchesSearch = !term
      || (member.name || '').toLowerCase().includes(term)
      || (member.employeeNumber || '').toLowerCase().includes(term)
      || (member.officeEmail || '').toLowerCase().includes(term);

    const matchesType = filterType === 'all' || member.type === filterType;
    const matchesStatus = filterStatus === 'all' || member.loginStatus === filterStatus;

    return matchesSearch && matchesType && matchesStatus;
  });

  const staffTypes = Array.from(new Set(staff.map((s) => s.type).filter(Boolean)));

  const getInitials = (name) => {
    return (name || '-')
      .split(' ')
      .filter(Boolean)
      .slice(0, 2)
      .map((s) => s[0])
      .join('')
      .toUpperCase() || 'NA';
  };

  const getImageUrl = (member) => {
    if (!member?.imagePath || imageErrors[member.staffId]) return null;
    return getImageURL(member.imagePath);
  };

  const handleImageError = (staffId) => {
    setImageErrors((prev) => ({ ...prev, [staffId]: true }));
  };

  const handleImageUploadClick = (member) => {
    if (!isAdmin) return;
    setStaffForImageUpload(member);
    fileInputRef.current?.click();
  };

  const handleFileChange = async (event) => {
    const file = event.target.files?.[0];
    if (!file || !staffForImageUpload) return;

    if (!file.type.startsWith('image/')) {
      Swal.fire('Invalid File', 'Please select an image file (JPEG, PNG, etc.)', 'error');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      Swal.fire('File Too Large', 'Please select an image smaller than 5MB', 'error');
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      setCropSrc(reader.result);
      setCrop({ x: 0, y: 0 });
      setZoom(1);
      setCroppedAreaPixels(null);
      setCropFileType(file.type || 'image/jpeg');
      setCropModalOpen(true);
    };
    reader.readAsDataURL(file);
  };

  const onCropComplete = (croppedArea, croppedAreaPx) => {
    setCroppedAreaPixels(croppedAreaPx);
  };

  const createImage = (url) => new Promise((resolve, reject) => {
    const image = new Image();
    image.addEventListener('load', () => resolve(image));
    image.addEventListener('error', (error) => reject(error));
    image.setAttribute('crossOrigin', 'anonymous');
    image.src = url;
  });

  const getCroppedImg = async (imageSrc, pixelCrop, fileType) => {
    const image = await createImage(imageSrc);
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    canvas.width = pixelCrop.width;
    canvas.height = pixelCrop.height;

    ctx.drawImage(
      image,
      pixelCrop.x,
      pixelCrop.y,
      pixelCrop.width,
      pixelCrop.height,
      0,
      0,
      pixelCrop.width,
      pixelCrop.height,
    );

    return new Promise((resolve, reject) => {
      canvas.toBlob((blob) => {
        if (!blob) {
          reject(new Error('Canvas is empty'));
          return;
        }
        const file = new File([blob], 'cropped-image.jpg', { type: fileType });
        resolve(file);
      }, fileType);
    });
  };

  const handleCropCancel = () => {
    setCropModalOpen(false);
    setCropSrc(null);
    setCroppedAreaPixels(null);
    setStaffForImageUpload(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleCropConfirm = async () => {
    if (!cropSrc || !croppedAreaPixels || !staffForImageUpload) {
      handleCropCancel();
      return;
    }

    try {
      setProcessing(staffForImageUpload.staffId);
      const croppedFile = await getCroppedImg(cropSrc, croppedAreaPixels, cropFileType || 'image/jpeg');
      const formData = new FormData();
      formData.append('image', croppedFile);

      const resp = await axiosInstance.post(`/admin/staff/${staffForImageUpload.staffId}/image`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      const data = resp?.data || {};
      if (!data.success) {
        throw new Error(data.message || 'Upload failed');
      }

      Swal.fire('Success', 'Staff image uploaded successfully', 'success');

      setStaff((prev) => prev.map((s) => (
        s.staffId === staffForImageUpload.staffId
          ? { ...s, imagePath: data.imagePath || s.imagePath }
          : s
      )));
      setImageErrors((prev) => ({ ...prev, [staffForImageUpload.staffId]: false }));
    } catch (err) {
      console.error('Error uploading image', err);
      Swal.fire('Upload Failed', err?.response?.data?.message || err.message || 'Failed to upload image', 'error');
    } finally {
      setProcessing(null);
      setCropModalOpen(false);
      setCropSrc(null);
      setCroppedAreaPixels(null);
      setStaffForImageUpload(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const toggleStaffStatus = async (staffId, currentStatus, staffName) => {
    if (!isAdmin || processing) return;

    const newStatus = currentStatus === 'active' ? 'blocked' : 'active';
    const actionText = newStatus === 'blocked' ? 'block' : 'activate';

    const result = await Swal.fire({
      title: `${actionText === 'block' ? 'Block' : 'Activate'} Staff Member?`,
      html: `Are you sure you want to <strong>${actionText}</strong> <br/><strong>${staffName}</strong>?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: newStatus === 'blocked' ? '#dc2626' : '#16a34a',
      cancelButtonColor: '#6b7280',
      confirmButtonText: `Yes, ${actionText}!`,
      cancelButtonText: 'Cancel',
      reverseButtons: true,
    });

    if (!result.isConfirmed) return;

    try {
      setProcessing(staffId);
      const resp = await axiosInstance.post(`/admin/staff/${staffId}/toggle-status`, { status: newStatus });
      const data = resp?.data || {};

      setStaff((prev) => prev.map((s) => (
        s.staffId === staffId
          ? { ...s, loginStatus: data.loginStatus || newStatus }
          : s
      )));

      Swal.fire({
        title: 'Success!',
        text: `Staff member has been ${newStatus === 'blocked' ? 'blocked' : 'activated'} successfully.`,
        icon: 'success',
        timer: 2000,
        showConfirmButton: false,
      });
    } catch (err) {
      console.error('Failed to toggle staff status', err);
      Swal.fire('Error', 'Failed to update staff status. Please try again.', 'error');
    } finally {
      setProcessing(null);
    }
  };

  const resetPassword = async (staffId, staffName) => {
    if (!isAdmin || processing) return;

    const result = await Swal.fire({
      title: 'Reset Password?',
      html: `Are you sure you want to reset the password for<br/><strong>${staffName}</strong>?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#f59e0b',
      cancelButtonColor: '#6b7280',
      confirmButtonText: 'Yes, reset password!',
      cancelButtonText: 'Cancel',
      reverseButtons: true,
    });

    if (!result.isConfirmed) return;

    try {
      setProcessing(staffId);
      const generated = generatePassword();
      const resp = await axiosInstance.post(`/admin/staff/${staffId}/reset-password`, { password: generated });
      const data = resp?.data || {};
      const password = data.password || generated;
      const name = data.staffName || staffName;

      Swal.fire({
        title: 'Password Reset Successfully!',
        html: `
          <div class="text-left space-y-4">
            <p class="text-sm text-gray-600 mb-4">The new password for <strong>${name}</strong> is:</p>
            <div class="flex items-center gap-2 bg-gray-100 p-4 rounded-lg">
              <input 
                id="password-field" 
                type="text" 
                value="${password}" 
                readonly 
                class="flex-1 bg-transparent border-none outline-none font-mono text-lg font-bold text-gray-900"
              />
              <button 
                id="copy-button" 
                class="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors flex items-center gap-2"
              >
                <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"></path>
                </svg>
                Copy
              </button>
            </div>
            <p class="text-xs text-amber-600 mt-2">⚠️ Make sure to save this password. It won't be shown again.</p>
          </div>
        `,
        icon: 'success',
        confirmButtonColor: '#3b82f6',
        confirmButtonText: 'Done',
        width: '600px',
        didOpen: () => {
          const popup = Swal.getPopup();
          if (!popup) return;
          const input = popup.querySelector('#password-field');
          const btn = popup.querySelector('#copy-button');
          if (!input || !btn) return;

          btn.addEventListener('click', async () => {
            try {
              const text = input.value || '';
              if (navigator.clipboard && navigator.clipboard.writeText) {
                await navigator.clipboard.writeText(text);
              } else {
                input.select();
                document.execCommand('copy');
              }

              btn.innerHTML = "<svg class='h-4 w-4' fill='none' stroke='currentColor' viewBox='0 0 24 24'><path stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M5 13l4 4L19 7'></path></svg> Copied!";
              setTimeout(() => {
                btn.innerHTML = "<svg class='h-4 w-4' fill='none' stroke='currentColor' viewBox='0 0 24 24'><path stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z'></path></svg> Copy";
              }, 2000);
            } catch (copyErr) {
              console.error('Failed to copy password', copyErr);
            }
          });
        },
      });
    } catch (err) {
      console.error('Failed to reset password', err);
      Swal.fire('Error!', 'Failed to reset password. Please try again.', 'error');
    } finally {
      setProcessing(null);
    }
  };

  const viewStaffDetails = (member) => {
    const genderIcon = member.gender === 'male' ? '👨' : member.gender === 'female' ? '👩' : '👤';
    const statusColor = member.loginStatus === 'active' ? 'text-green-600' : 'text-red-600';
    const statusIcon = member.loginStatus === 'active' ? '✅' : '❌';
    const initialsForModal = getInitials(member.name);
    const imgUrl = getImageUrl(member);

    Swal.fire({
      title: `<div class="flex items-center justify-center gap-3">
                <span class="text-4xl">${genderIcon}</span>
                <span>${member.title ? member.title + '. ' : ''}${member.name}</span>
              </div>`,
      html: `
        <div class="text-left space-y-4 p-4">
          ${imgUrl ? `
            <div class="flex justify-center mb-4">
              <img 
                src="${imgUrl}" 
                alt="${member.name}"
                class="h-32 w-32 rounded-full object-cover border-4 border-indigo-100"
                onerror="this.onerror=null; this.remove(); var p=this.parentElement; if(p){ var d=document.createElement('div'); d.className='h-32 w-32 rounded-full flex items-center justify-center bg-gray-200 text-gray-700 text-2xl font-bold border-4 border-indigo-100'; d.textContent='${initialsForModal}'; p.appendChild(d);}"
              />
            </div>
          ` : `
            <div class="flex justify-center mb-4">
              <div class="h-32 w-32 rounded-full flex items-center justify-center bg-gray-200 text-gray-700 text-2xl font-bold border-4 border-indigo-100">${initialsForModal}</div>
            </div>
          `}

          <div class="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-lg p-4 border border-indigo-200">
            <h3 class="text-lg font-bold text-indigo-900 mb-3 flex items-center gap-2">
              <svg class="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
              </svg>
              Basic Information
            </h3>
            <div class="grid grid-cols-2 gap-3 text-sm">
              ${member.employeeNumber ? `
                <div>
                  <p class="text-gray-500 text-xs font-medium">Employee Number</p>
                  <p class="text-gray-900 font-semibold">${member.employeeNumber}</p>
                </div>
              ` : ''}
              ${member.gender ? `
                <div>
                  <p class="text-gray-500 text-xs font-medium">Gender</p>
                  <p class="text-gray-900 font-semibold capitalize">${member.gender}</p>
                </div>
              ` : ''}
              ${member.position ? `
                <div class="col-span-2">
                  <p class="text-gray-500 text-xs font-medium">Position</p>
                  <p class="text-gray-900 font-semibold">${member.position}</p>
                </div>
              ` : ''}
              ${member.type ? `
                <div class="col-span-2">
                  <p class="text-gray-500 text-xs font-medium mb-1.5">Staff Type</p>
                  <span class="inline-block rounded-lg border px-3 py-1.5 text-xs font-medium shadow-sm ${getStaffTypeColor(member.type).replace(/'/g, '')}">
                    ${member.type}
                  </span>
                </div>
              ` : ''}
            </div>
          </div>

          <div class="bg-gradient-to-r from-blue-50 to-cyan-50 rounded-lg p-4 border border-blue-200">
            <h3 class="text-lg font-bold text-blue-900 mb-3 flex items-center gap-2">
              <svg class="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"></path>
              </svg>
              Contact Information
            </h3>
            <div class="space-y-3 text-sm">
              ${member.officeEmail ? `
                <div class="flex items-start gap-2">
                  <svg class="h-4 w-4 text-blue-600 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"></path>
                  </svg>
                  <div>
                    <p class="text-gray-500 text-xs">Office Email</p>
                    <p class="text-gray-900 font-medium break-all">${member.officeEmail}</p>
                  </div>
                </div>
              ` : ''}
              ${member.gmail ? `
                <div class="flex items-start gap-2">
                  <svg class="h-4 w-4 text-blue-600 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"></path>
                  </svg>
                  <div>
                    <p class="text-gray-500 text-xs">Gmail</p>
                    <p class="text-gray-900 font-medium break-all">${member.gmail}</p>
                  </div>
                </div>
              ` : ''}
              ${member.mobileNumber ? `
                <div class="flex items-start gap-2">
                  <svg class="h-4 w-4 text-blue-600 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"></path>
                  </svg>
                  <div>
                    <p class="text-gray-500 text-xs">Mobile Number</p>
                    <p class="text-gray-900 font-medium">${member.mobileNumber}</p>
                  </div>
                </div>
              ` : ''}
            </div>
          </div>

          ${member.loginStatus ? `
            <div class="bg-gradient-to-r from-gray-50 to-slate-50 rounded-lg p-4 border border-gray-200">
              <h3 class="text-lg font-bold text-gray-900 mb-3 flex items-center gap-2">
                <svg class="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"></path>
                </svg>
                Account Status
              </h3>
              <div class="flex items-center gap-2">
                <span class="text-2xl">${statusIcon}</span>
                <span class="text-lg font-bold ${statusColor} capitalize">${member.loginStatus}</span>
              </div>
            </div>
          ` : ''}

          <div class="text-center text-xs text-gray-400 pt-2 border-t border-gray-200">
            Staff ID: ${member.staffId}
          </div>
        </div>
      `,
      width: '600px',
      showCloseButton: true,
      showConfirmButton: true,
      confirmButtonText: 'Close',
      confirmButtonColor: '#3b82f6',
      customClass: {
        popup: 'rounded-2xl',
        title: 'text-2xl',
        htmlContainer: 'staff-details-modal',
      },
    });
  };

  return (
    <DashboardLayout title="All Staff">
      {/* Header with Actions */}
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">All Staff</h2>
          <p className="mt-1 text-sm text-gray-600">
            Viewing {filteredStaff.length} of {totalStaff} staff members
          </p>
        </div>
        {isAdmin && (
          <div className="flex items-center gap-3">
            {/* Download dropdown */}
            <div className="relative" ref={downloadMenuRef}>
              <button
                type="button"
                onClick={() => setShowDownloadMenu((prev) => !prev)}
                className="inline-flex items-center gap-2 rounded-lg border border-emerald-600 bg-white px-4 py-2 text-sm font-medium text-emerald-700 shadow-sm hover:bg-emerald-50 transition-colors"
              >
                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                </svg>
                Download
                <svg
                  className={`h-4 w-4 transition-transform duration-200 ${showDownloadMenu ? 'rotate-180' : ''}`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>

              {showDownloadMenu && (
                <div className="absolute right-0 z-20 mt-2 w-56 origin-top-right rounded-lg border border-gray-200 bg-white shadow-lg ring-1 ring-black/5">
                  <div className="px-3 py-2 border-b border-gray-100">
                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Export Staff to Excel</p>
                  </div>
                  <div className="py-1">
                    <button
                      type="button"
                      onClick={() => downloadStaffExcel('active')}
                      className="flex w-full items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-emerald-50 hover:text-emerald-700 transition-colors"
                    >
                      <span className="flex h-7 w-7 items-center justify-center rounded-full bg-emerald-100">
                        <svg className="h-4 w-4 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      </span>
                      <div className="text-left">
                        <p className="font-medium">Active Staff Only</p>
                        <p className="text-xs text-gray-400">Export currently active staff</p>
                      </div>
                    </button>
                    <button
                      type="button"
                      onClick={() => downloadStaffExcel('all')}
                      className="flex w-full items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-indigo-50 hover:text-indigo-700 transition-colors"
                    >
                      <span className="flex h-7 w-7 items-center justify-center rounded-full bg-indigo-100">
                        <svg className="h-4 w-4 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                        </svg>
                      </span>
                      <div className="text-left">
                        <p className="font-medium">All Staff</p>
                        <p className="text-xs text-gray-400">Export all staff regardless of status</p>
                      </div>
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Add Staff */}
            <button
              type="button"
              onClick={() => setShowAddModal(true)}
              className="inline-flex items-center rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-indigo-700"
            >
              <svg className="mr-2 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 4v16m8-8H4"
                />
              </svg>
              Add Staff
            </button>
          </div>
        )}
      </div>

      {/* Filters - Admin only */}
      {isAdmin && (
        <div className="mb-6 flex flex-wrap gap-4">
          <input
            type="text"
            placeholder="Search staff by name, email, or employee number..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="flex-1 min-w-[250px] rounded-lg border border-gray-300 px-4 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
          />
          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            className="rounded-lg border border-gray-300 px-4 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
          >
            <option value="all">All Types</option>
            {staffTypes.map((type) => (
              <option key={type} value={type}>
                {type}
              </option>
            ))}
          </select>
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="rounded-lg border border-gray-300 px-4 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
          >
            <option value="all">All Status</option>
            <option value="active">Active</option>
            <option value="blocked">Blocked</option>
          </select>
        </div>
      )}

      {/* Staff Grid */}
      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
        {filteredStaff.map((member) => {
          const imgUrl = getImageUrl(member);
          return (
            <div
              key={member.staffId}
              className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm transition-all hover:shadow-md"
            >
              <div className="relative h-[300px] bg-gradient-to-br from-indigo-50 to-purple-50">
                {imgUrl ? (
                  <img
                    src={imgUrl}
                    alt={member.name}
                    className="h-full w-full object-cover"
                    onError={() => handleImageError(member.staffId)}
                  />
                ) : (
                  <div className="flex h-full w-full items-center justify-center bg-gray-100">
                    <div className="flex items-center justify-center h-24 w-24 rounded-full bg-gray-300 text-gray-700 text-2xl font-bold shadow">
                      {getInitials(member.name)}
                    </div>
                  </div>
                )}
                {member.loginStatus && (
                  <div className="absolute top-2 right-2">
                    <span
                      className={`rounded-full px-2 py-1 text-xs font-medium ${
                        member.loginStatus === 'active'
                          ? 'bg-green-100 text-green-800'
                          : 'bg-red-100 text-red-800'
                      }`}
                    >
                      {member.loginStatus}
                    </span>
                  </div>
                )}
              </div>

              <div className="p-4">
                <div className="mb-2">
                  <h3 className="text-lg font-semibold text-gray-900">
                    {member.title ? `${member.title}. ` : ''}
                    {member.name}
                  </h3>
                  {member.position && (
                    <p className="text-sm font-medium text-indigo-600">{member.position}</p>
                  )}
                  {member.employeeNumber && (
                    <p className="text-xs text-gray-500">EMP: {member.employeeNumber}</p>
                  )}
                </div>

                <div className="space-y-1 border-t border-gray-100 pt-3">
                  {member.officeEmail && (
                    <div className="flex items-center gap-2 text-xs text-gray-600">
                      <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                        />
                      </svg>
                      <span className="truncate">{member.officeEmail}</span>
                    </div>
                  )}
                  {member.mobileNumber && (
                    <div className="flex items-center gap-2 text-xs text-gray-600">
                      <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                        />
                      </svg>
                      <span>{member.mobileNumber}</span>
                    </div>
                  )}
                  {member.type && (
                    <div className="mt-2">
                      <span
                        className={`inline-block rounded-lg border px-3 py-1.5 text-xs font-medium shadow-sm ${getStaffTypeColor(
                          member.type,
                        )}`}
                      >
                        {member.type}
                      </span>
                    </div>
                  )}
                </div>

                <div className="mt-4 flex flex-col gap-2">
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => viewStaffDetails(member)}
                      className="flex-1 rounded-lg border border-indigo-600 px-3 py-2 text-xs font-medium text-indigo-600 hover:bg-indigo-50"
                    >
                      View Details
                    </button>
                    {isAdmin && (
                      <button
                        type="button"
                        onClick={() => {
                          setStaffForEdit(member);
                          setShowEditModal(true);
                        }}
                        className="rounded-lg border border-blue-600 bg-blue-50 px-3 py-2 text-xs text-blue-600 hover:bg-blue-100"
                        title="Edit Staff"
                      >
                        <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L12 18l-4 1 1-4 9.586-9.586z"
                          />
                        </svg>
                      </button>
                    )}
                    {isAdmin && (
                      <button
                        type="button"
                        onClick={() => handleImageUploadClick(member)}
                        className="rounded-lg border border-green-600 bg-green-50 px-3 py-2 text-xs text-green-600 hover:bg-green-100"
                        title="Upload Image"
                        disabled={processing === member.staffId}
                      >
                        <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                          />
                        </svg>
                      </button>
                    )}
                  </div>
                  {isAdmin && member.loginStatus && (
                    <>
                      <button
                        type="button"
                        onClick={() => resetPassword(member.staffId, member.name)}
                        disabled={processing === member.staffId}
                        className={`w-full rounded-lg px-3 py-2 text-xs font-medium transition-colors bg-amber-100 text-amber-700 hover:bg-amber-200 border border-amber-300 ${
                          processing === member.staffId ? 'opacity-50 cursor-not-allowed' : ''
                        }`}
                      >
                        {processing === member.staffId ? (
                          <span className="flex items-center justify-center gap-2">
                            <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                              <circle
                                className="opacity-25"
                                cx="12"
                                cy="12"
                                r="10"
                                stroke="currentColor"
                                strokeWidth="4"
                              />
                              <path
                                className="opacity-75"
                                fill="currentColor"
                                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                              />
                            </svg>
                            Processing...
                          </span>
                        ) : (
                          <span className="flex items-center justify-center gap-1">
                            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z"
                              />
                            </svg>
                            Reset Password
                          </span>
                        )}
                      </button>
                      <button
                        type="button"
                        onClick={() => toggleStaffStatus(member.staffId, member.loginStatus, member.name)}
                        disabled={processing === member.staffId}
                        className={`w-full rounded-lg px-3 py-2 text-xs font-medium transition-colors ${
                          member.loginStatus === 'active'
                            ? 'bg-red-100 text-red-700 hover:bg-red-200 border border-red-300'
                            : 'bg-green-100 text-green-700 hover:bg-green-200 border border-green-300'
                        } ${processing === member.staffId ? 'opacity-50 cursor-not-allowed' : ''}`}
                      >
                        {processing === member.staffId ? (
                          <span className="flex items-center justify-center gap-2">
                            <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                              <circle
                                className="opacity-25"
                                cx="12"
                                cy="12"
                                r="10"
                                stroke="currentColor"
                                strokeWidth="4"
                              />
                              <path
                                className="opacity-75"
                                fill="currentColor"
                                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                              />
                            </svg>
                            Processing...
                          </span>
                        ) : member.loginStatus === 'active' ? (
                          <span className="flex items-center justify-center gap-1">
                            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636"
                              />
                            </svg>
                            Block Staff
                          </span>
                        ) : (
                          <span className="flex items-center justify-center gap-1">
                            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                              />
                            </svg>
                            Activate Staff
                          </span>
                        )}
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {filteredStaff.length === 0 && (
        <div className="mt-8 text-center">
          <svg
            className="mx-auto h-12 w-12 text-gray-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
            />
          </svg>
          <h3 className="mt-2 text-sm font-medium text-gray-900">No staff found</h3>
          <p className="mt-1 text-sm text-gray-500">Try adjusting your search or filter criteria.</p>
        </div>
      )}

      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleFileChange}
        className="hidden"
      />

      {cropModalOpen && cropSrc && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
          <div className="w-full max-w-lg rounded-xl bg-white p-4 shadow-xl">
            <h3 className="mb-4 text-lg font-semibold text-gray-900">Crop Staff Image</h3>
            <div className="relative h-64 w-full overflow-hidden rounded-lg bg-black/5">
              <Cropper
                image={cropSrc}
                crop={crop}
                zoom={zoom}
                aspect={1}
                onCropChange={setCrop}
                onZoomChange={setZoom}
                onCropComplete={onCropComplete}
              />
            </div>
            <div className="mt-4 flex items-center justify-between gap-3">
              <input
                type="range"
                min={1}
                max={3}
                step={0.1}
                value={zoom}
                onChange={(e) => setZoom(Number(e.target.value))}
                className="flex-1"
              />
              <span className="ml-2 text-xs text-gray-500">Zoom</span>
            </div>
            <div className="mt-4 flex justify-end gap-2">
              <button
                type="button"
                onClick={handleCropCancel}
                className="rounded-lg border border-gray-300 px-4 py-2 text-xs font-medium text-gray-700 hover:bg-gray-50"
                disabled={processing != null}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleCropConfirm}
                className="rounded-lg bg-indigo-600 px-4 py-2 text-xs font-medium text-white hover:bg-indigo-700 disabled:opacity-50"
                disabled={processing != null}
              >
                {processing != null ? 'Uploading...' : 'Crop & Upload'}
              </button>
            </div>
          </div>
        </div>
      )}

      {isAdmin && showAddModal && (
        <StaffFormModal
          mode="add"
          positions={positions}
          onClose={() => setShowAddModal(false)}
          onSaved={(created) => {
            setStaff((prev) => [...prev, created]);
            setTotalStaff((prev) => prev + 1);
            setShowAddModal(false);
          }}
        />
      )}

      {isAdmin && showEditModal && staffForEdit && (
        <StaffFormModal
          mode="edit"
          positions={positions}
          initialStaff={staffForEdit}
          onClose={() => {
            setShowEditModal(false);
            setStaffForEdit(null);
          }}
          onSaved={(updated) => {
            setStaff((prev) => prev.map((s) => (s.staffId === updated.staffId ? updated : s)));
            setShowEditModal(false);
            setStaffForEdit(null);
          }}
        />
      )}
    </DashboardLayout>
  );
}

function StaffFormModal({ mode, positions, initialStaff, onClose, onSaved }) {
  const isEdit = mode === 'edit';
  const [form, setForm] = useState({
    name: initialStaff?.name || '',
    title: initialStaff?.title || '',
    gender: initialStaff?.gender || '',
    employeeNumber: initialStaff?.employeeNumber || '',
    officeEmail: initialStaff?.officeEmail || '',
    gmail: initialStaff?.gmail || '',
    mobileNumber: initialStaff?.mobileNumber || '',
    type: initialStaff?.type || '',
    positionId: initialStaff?.positionId || '',
  });
  const [submitting, setSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) {
      Swal.fire('Validation', 'Name is required', 'warning');
      return;
    }
    try {
      setSubmitting(true);
      const payload = {
        name: form.name.trim(),
        title: form.title || null,
        gender: form.gender || null,
        employeeNumber: form.employeeNumber || null,
        officeEmail: form.officeEmail || null,
        gmail: form.gmail || null,
        mobileNumber: form.mobileNumber || null,
        type: form.type || null,
        positionId: form.positionId || null,
      };

      let resp;
      if (isEdit && initialStaff?.staffId) {
        resp = await axiosInstance.put(`/admin/staff/${initialStaff.staffId}`, payload);
      } else {
        resp = await axiosInstance.post('/admin/staff', payload);
      }

      const data = resp?.data || {};
      if (!data.success || !data.staff) {
        throw new Error(data.message || 'Failed to save staff');
      }

      onSaved(data.staff);
      Swal.fire('Success', `Staff ${isEdit ? 'updated' : 'created'} successfully`, 'success');
    } catch (err) {
      console.error('Failed to save staff', err);
      Swal.fire('Error', err?.response?.data?.message || err.message || 'Failed to save staff', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
      <div className="w-full max-w-lg rounded-xl bg-white p-5 shadow-xl">
        <div className="mb-4 flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-900">
            {isEdit ? 'Edit Staff' : 'Add Staff'}
          </h3>
          <button
            type="button"
            onClick={onClose}
            className="rounded-full p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
            disabled={submitting}
          >
            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="mb-1 block text-xs font-medium text-gray-700">Title</label>
              <select
                name="title"
                value={form.title}
                onChange={handleChange}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-xs focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
              >
                <option value="">Select</option>
                <option value="Mr">Mr</option>
                <option value="Mrs">Mrs</option>
                <option value="Miss">Miss</option>
                <option value="Ms">Ms</option>
                <option value="Dr">Dr</option>
              </select>
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-gray-700">Gender</label>
              <select
                name="gender"
                value={form.gender}
                onChange={handleChange}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-xs focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
              >
                <option value="">Select</option>
                <option value="male">Male</option>
                <option value="female">Female</option>
              </select>
            </div>
          </div>

          <div>
            <label className="mb-1 block text-xs font-medium text-gray-700">Name *</label>
            <input
              type="text"
              name="name"
              value={form.name}
              onChange={handleChange}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-xs focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
              required
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="mb-1 block text-xs font-medium text-gray-700">Employee Number</label>
              <input
                type="text"
                name="employeeNumber"
                value={form.employeeNumber}
                onChange={handleChange}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-xs focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-gray-700">Mobile Number</label>
              <input
                type="text"
                name="mobileNumber"
                value={form.mobileNumber}
                onChange={handleChange}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-xs focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="mb-1 block text-xs font-medium text-gray-700">Office Email</label>
              <input
                type="email"
                name="officeEmail"
                value={form.officeEmail}
                onChange={handleChange}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-xs focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-gray-700">Gmail</label>
              <input
                type="email"
                name="gmail"
                value={form.gmail}
                onChange={handleChange}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-xs focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="mb-1 block text-xs font-medium text-gray-700">Position</label>
              <select
                name="positionId"
                value={form.positionId}
                onChange={handleChange}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-xs focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
              >
                <option value="">Select position</option>
                {positions.map((pos) => (
                  <option key={pos.positionId} value={pos.positionId}>
                    {pos.positionName}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-gray-700">Staff Type</label>
              <input
                type="text"
                name="type"
                value={form.type}
                onChange={handleChange}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-xs focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                placeholder="e.g. Admin, Lecturer"
              />
            </div>
          </div>

          <div className="mt-4 flex justify-end gap-2">
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg border border-gray-300 px-4 py-2 text-xs font-medium text-gray-700 hover:bg-gray-50"
              disabled={submitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="rounded-lg bg-indigo-600 px-4 py-2 text-xs font-medium text-white hover:bg-indigo-700 disabled:opacity-50"
              disabled={submitting}
            >
              {submitting ? (isEdit ? 'Updating...' : 'Creating...') : (isEdit ? 'Update Staff' : 'Create Staff')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
